import { fontTokensForBranding } from './fonts';

/* ─── Theme Definitions ─── */

export const SECUREVIEW = {
  C: {
    bg: '#0a0c0f', s1: '#111418', s2: '#161b22', s3: '#1a2030',
    b1: '#1e2530', b2: '#141820',
    t1: '#e2e8f0', t2: '#64748b', t3: '#2d3748',
    red: '#ef4444', redBg: 'rgba(239,68,68,0.08)',
    org: '#f97316', orgBg: 'rgba(249,115,22,0.08)',
    amb: '#f59e0b', ambBg: 'rgba(245,158,11,0.08)',
    sky: '#0ea5e9', skyBg: 'rgba(14,165,233,0.08)',
    gry: '#64748b', gryBg: 'rgba(100,116,139,0.08)',
    grn: '#22c55e', grnBg: 'rgba(34,197,94,0.08)',
    pur: '#a855f7', purBg: 'rgba(168,85,247,0.1)',
    gld: '#eab308', gldBg: 'rgba(234,179,8,0.1)',
    blu: '#3b82f6', bluBg: 'rgba(59,130,246,0.1)',
    brand: '#0ea5e9', brandBg: 'rgba(14,165,233,0.08)',
  },
  FONTS: fontTokensForBranding('default'),
  RADIUS: { sm: 4, md: 6, lg: 8, full: 9999 },
  branding: {
    portalName: 'Sentinel Command',
    tagline: 'Cybersecurity Command Portal',
    description: 'Unified vulnerability management, compliance monitoring, and attack surface intelligence for managed security service providers.',
    logoLetter: 'S',
    logoGradient: 'linear-gradient(135deg,#ef4444,#7c3aed)',
    buttonGradient: 'linear-gradient(135deg,#ef4444,#7c3aed)',
    version: 'Sentinel Command · v3.2.1 · SOC 2 Type II Certified',
    features: [],
  },
};

export const CONNECTON = {
  C: {
    bg: '#f8f9fb', s1: '#ffffff', s2: '#f1f3f5', s3: '#e9ecef',
    b1: '#dee2e6', b2: '#e9ecef',
    t1: '#1a1a2e', t2: '#6b7280', t3: '#c9cdd3',
    red: '#dc2626', redBg: 'rgba(220,38,38,0.07)',
    org: '#ea580c', orgBg: 'rgba(234,88,12,0.07)',
    amb: '#d97706', ambBg: 'rgba(217,119,6,0.07)',
    sky: '#0284c7', skyBg: 'rgba(2,132,199,0.07)',
    gry: '#6b7280', gryBg: 'rgba(107,114,128,0.07)',
    grn: '#16a34a', grnBg: 'rgba(22,163,74,0.07)',
    brand: '#0d9488', brandBg: 'rgba(13,148,136,0.1)',
    pur: '#9333ea', purBg: 'rgba(147,51,234,0.08)',
    gld: '#ca8a04', gldBg: 'rgba(202,138,4,0.08)',
    blu: '#2563eb', bluBg: 'rgba(37,99,235,0.08)',
  },
  FONTS: fontTokensForBranding('default'),
  RADIUS: { sm: 4, md: 6, lg: 8, full: 9999 },
  branding: {
    portalName: 'ConnectOn',
    tagline: 'Client Security Portal',
    description: 'Transparent security visibility, compliance reporting, and real-time threat intelligence for your organization.',
    logoLetter: 'C',
    logoGradient: 'linear-gradient(135deg,#0d9488,#0284c7)',
    buttonGradient: 'linear-gradient(135deg,#0d9488,#0284c7)',
    version: 'ConnectOn · v3.2.1 · SOC 2 Type II Certified',
    features: [],
  },
};

/* ─── Mock org-to-theme mapping (backend would supply this) ─── */

export const ORG_THEMES = {
  'clearpathit.com':    { theme: 'secureview', org: 'ClearPath IT',       role: 'MSP Admin' },
  'sentinelcommand.io': { theme: 'secureview', org: 'Sentinel Command',   role: 'MSP Admin' },
  'sss.com':            { theme: 'secureview', org: 'Supreme Security',   role: 'MSP Admin' },
  'acmecorp.com':       { theme: 'connecton',  org: 'Acme Corporation',   role: 'Client Admin' },
  'globexinc.com':      { theme: 'connecton',  org: 'Globex Inc',         role: 'Client Viewer' },
  'initech.com':        { theme: 'connecton',  org: 'Initech Solutions',  role: 'Client Admin' },
};

export const THEMES = { secureview: SECUREVIEW, connecton: CONNECTON };

/** Resolve an email address to its org theme config. Returns secureview default. */
export function resolveEmailTheme(email) {
  if (!email) return { theme: 'secureview', org: 'Unknown', role: 'Viewer' };
  const domain = email.split('@')[1]?.toLowerCase();
  return ORG_THEMES[domain] || { theme: 'secureview', org: domain, role: 'Viewer' };
}

import { validateContrast, luminance } from '../utils/colorContrast';

const isLight = (color) => {
  if (!color) return false;
  const hex = color.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return (r * 0.299 + g * 0.587 + b * 0.114) > 128;
};

/** Build a theme object from branding API response. Falls back to SECUREVIEW. */
export function buildThemeFromBranding(branding) {
  if (!branding || (!branding.primaryColor && !branding.backgroundColor)) {
    return { ...SECUREVIEW, FONTS: fontTokensForBranding(branding?.fontFamily) };
  }
  const lightBg = isLight(branding.backgroundColor);
  // Derive surface colors when not explicitly set — use light defaults for light backgrounds
  const bg = branding.backgroundColor || SECUREVIEW.C.bg;
  const s1 = branding.surfaceColor || (lightBg ? '#ffffff' : SECUREVIEW.C.s1);
  const s2 = branding.surfaceColor || (lightBg ? '#f1f5f9' : SECUREVIEW.C.s2);

  // Validate text/accent colors against background for WCAG AA contrast
  const t1 = validateContrast(branding.textColor || SECUREVIEW.C.t1, bg);
  const t2Raw = lightBg ? '#475569' : (branding.textColor ? branding.textColor + 'aa' : SECUREVIEW.C.t2);
  const brandColor = validateContrast(branding.primaryColor || SECUREVIEW.C.brand, s1);
  const skyColor = validateContrast(branding.primaryColor || SECUREVIEW.C.sky, s1);
  const purColor = validateContrast(branding.secondaryColor || SECUREVIEW.C.pur, s1);

  return {
    C: {
      ...SECUREVIEW.C,
      bg,
      s1,
      s2,
      s3: lightBg ? '#e9ecef' : SECUREVIEW.C.s3,
      t1,
      t2: t2Raw,
      t3: lightBg ? '#94a3b8' : SECUREVIEW.C.t3,
      b1: lightBg ? '#e2e8f0' : SECUREVIEW.C.b1,
      b2: lightBg ? '#f1f5f9' : SECUREVIEW.C.b2,
      brand: brandColor,
      brandBg: (branding.primaryColor || SECUREVIEW.C.brand) + '14',
      sky: skyColor,
      skyBg: (branding.primaryColor || SECUREVIEW.C.sky) + '14',
      pur: purColor,
      purBg: (branding.secondaryColor || SECUREVIEW.C.pur) + '1a',
    },
    FONTS: fontTokensForBranding(branding.fontFamily),
    RADIUS: SECUREVIEW.RADIUS,
    branding: {
      portalName: branding.portalName || SECUREVIEW.branding.portalName,
      tagline: branding.tagline || SECUREVIEW.branding.tagline,
      description: branding.description || SECUREVIEW.branding.description,
      logoLetter: branding.logoLetter || SECUREVIEW.branding.logoLetter,
      logoUrl: branding.logoUrl || '',
      logoGradient: `linear-gradient(135deg,${branding.primaryColor || '#ef4444'},${branding.secondaryColor || '#7c3aed'})`,
      buttonGradient: `linear-gradient(135deg,${branding.primaryColor || '#ef4444'},${branding.secondaryColor || '#7c3aed'})`,
      version: `${branding.portalName || 'Sentinel Command'} · v3.2.1`,
      features: branding.features ?? [],
    },
  };
}
